import React, { MouseEvent, Component } from 'react';
import Tooltip from 'rc-tooltip';
import path from 'path-browserify';
import cn from 'classnames';

import { Renamer } from '../../renamer';
import { GenConfirmationModal } from '../../../../atoms/confirmation-modal/genesis';
import { TreeNodeProps, TreeNode, TreeModel, TreeModelContext } from '../../../../atoms/tree-layer';
import { ActionBox, Action } from '../action-box';
import { Icon } from '../../../../atoms/icon';

import { DraggableTreeNode } from '../../../../atoms/dragDrop';
import { Indent } from '../indent';
import { Filename } from '../filename';

import { AdditionTypes } from './folder-view';
import styles from './folder-view.module.scss';

export type FolderTitleProps = {
	onOpenChange?: (isOpen: boolean) => void;
	isOpen: boolean;
} & {
	onStartAdding: (type?: AdditionTypes) => any;
} & {
	node: TreeNode<undefined>;
	depth: number;
};

export class FolderTitle extends Component<FolderTitleProps> {
	state = {
		showDeleteConfirmation: false,
		isRenaming: false,
	};

	static contextType = TreeModelContext;
	context!: TreeModel;

	private handleAddFile = (e: MouseEvent<HTMLSpanElement>) => {
		e.stopPropagation();
		this.props.onOpenChange && this.props.onOpenChange(true);
		this.props.onStartAdding(AdditionTypes.file);
	};

	private handleAddFolder = (e: MouseEvent<HTMLSpanElement>) => {
		e.stopPropagation();
		this.props.onOpenChange && this.props.onOpenChange(true);
		this.props.onStartAdding(AdditionTypes.folder);
	};

	private handleDeleteFolder = (e: MouseEvent<HTMLSpanElement>) => {
		e.stopPropagation();
		this.setState({ showDeleteConfirmation: true });
	};

	private handleCancelDelete = () => {
		this.setState({ showDeleteConfirmation: false });
	};

	private handleConfirmDelete = () => {
		this.setState({ showDeleteConfirmation: false });

		const { node } = this.props;
		const treeModel = this.context;

		treeModel.unlink(node.id);
	};

	private handleRename = (nextName: string) => {
		const treeModel = this.context;
		const { node } = this.props;
		const { id } = node;

		this.setState({ isRenaming: false });

		if (!nextName) return;
		const basename = path.basename(id);
		const dirname = path.dirname(id);
		if (nextName === basename) return;

		const next = path.join(dirname, nextName);

		return treeModel.rename(id, next);
	};

	private handleAbortRename = () => {
		this.setState({ isRenaming: false });
	};

	private validateSelfRename = (nextName: string, prevName?: string) => {
		const treeModel = this.context;
		const { node } = this.props;

		const dirname = path.dirname(node.id);

		return (
			treeModel.isValidFilename(nextName) &&
			treeModel.canRename(path.join(dirname, nextName), path.join(dirname, prevName))
		);
	};

	private handleStartRename = (event: MouseEvent) => {
		event.stopPropagation();
		this.setState({ isRenaming: true });
	};

	private handleToggleOpenness = (event: MouseEvent<HTMLDivElement>) => {
		const { onOpenChange, isOpen } = this.props;
		onOpenChange && onOpenChange(!isOpen);
	};

	render() {
		const { node, depth, isOpen } = this.props;
		const { isRenaming } = this.state;
		const { id } = node;

		const basename: string = path.basename(id) || id; //should show '/' for '/' folder

		return (
			<DraggableTreeNode node={node}>
				<Indent
					depth={depth}
					className={cn(styles.folderItem)}
					onClick={this.handleToggleOpenness}
				>
					<Icon
						of={isOpen ? 'folder-open' : 'folder-close'}
						className={styles.folderIcon}
					/>

					{isRenaming && (
						<Renamer
							value={basename}
							validate={this.validateSelfRename}
							onSubmit={this.handleRename}
							onAbort={this.handleAbortRename}
						/>
					)}
					{!isRenaming && <Filename>{basename}</Filename>}
					{!isRenaming && (
						<ActionBox>
							<Tooltip overlay="new file" placement="bottom" mouseLeaveDelay={0}>
								<Action onClick={this.handleAddFile}>
									<Icon of="new-file" />
								</Action>
							</Tooltip>{' '}
							<Tooltip
								overlay="new folder"
								placement="bottom"
								mouseLeaveDelay={0}
							>
								<Action onClick={this.handleAddFolder}>
									<Icon of="new-folder" />
								</Action>
							</Tooltip>{' '}
							<Tooltip overlay="rename" placement="bottom" mouseLeaveDelay={0}>
								<Action onClick={this.handleStartRename}>
									<Icon of="pencil" />
								</Action>
							</Tooltip>{' '}
							<Tooltip overlay="delete" placement="bottom" mouseLeaveDelay={0}>
								<Action onClick={this.handleDeleteFolder}>
									<Icon of="trash" />
								</Action>
							</Tooltip>
						</ActionBox>
					)}
				</Indent>

				<GenConfirmationModal
					isOpen={this.state.showDeleteConfirmation}
					onCancel={this.handleCancelDelete}
					onConfirm={this.handleConfirmDelete}
					title="Are you sure you want to delete this folder?"
					confirm="Delete"
					cancel="Cancel"
				>
					<div>{id}</div>
				</GenConfirmationModal>
			</DraggableTreeNode>
		);
	}
}
